import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePlan from '../hooks/usePlan';
import Logo from './Logo';

const PLAN_BADGE = {
  gratis: { text: 'Plano Gratuito', className: 'text-orange-700 bg-orange-100' },
  basico: { text: 'Plano Básico', className: 'text-blue-700 bg-blue-100' },
  pro: { text: 'Plano Pro', className: 'text-green-700 bg-green-100' },
  admin: { text: 'Administrador', className: 'text-purple-700 bg-purple-100' },
};

function Header() {
  const { user, logout } = useAuth();
  const { plan } = usePlan();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    setMenuOpen(false);
    setMobileMenuOpen(false);
    await logout();
    navigate('/login', { replace: true });
  };

  const badge = PLAN_BADGE[plan.id] || PLAN_BADGE.gratis;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <Link to="/" className="inline-block no-underline">
          <Logo size="sm" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {!user && (
            <>
              <Link to="/blog" className="text-sm font-medium text-muted-foreground hover:text-secondary transition-colors no-underline">
                Blog
              </Link>
              <Link to="/login" className="text-sm font-medium text-muted-foreground hover:text-secondary transition-colors no-underline">
                Entrar
              </Link>
              <Link to="/orcamento/novo" className="no-underline">
                <button className="btn-cta text-sm px-5 py-2.5">
                  Calcular Agora
                </button>
              </Link>
            </>
          )}

          {user && (
            <>
              <Link to="/blog" className="text-sm font-medium text-muted-foreground hover:text-secondary transition-colors no-underline">
                Blog
              </Link>
              <Link to="/meus-orcamentos" className="text-sm font-medium text-muted-foreground hover:text-secondary transition-colors no-underline">
                Meus Orçamentos
              </Link>
              <Link to="/planos" className="text-sm font-medium text-muted-foreground hover:text-secondary transition-colors no-underline">
                Planos
              </Link>
              <Link to="/orcamento/novo" className="no-underline">
                <button className="btn-cta text-sm px-5 py-2.5">
                  Calcular Agora
                </button>
              </Link>

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-muted transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-accent">
                      {user.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-foreground hidden sm:block">
                    {user.name?.split(' ')[0]}
                  </span>
                  <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-56 bg-card rounded-2xl border border-border shadow-elevated z-20 py-2">
                      <div className="px-4 py-3 border-b border-border">
                        <p className="text-sm font-semibold text-foreground truncate">{user.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${badge.className}`}>
                          {badge.text}
                        </span>
                      </div>
                      <button
                        onClick={() => { setMenuOpen(false); navigate('/planos'); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted cursor-pointer flex items-center gap-2"
                      >
                        Meu plano
                      </button>
                      {plan.id === 'admin' && (
                        <>
                          <div className="border-t border-border my-1" />
                          <button onClick={() => { setMenuOpen(false); navigate('/admin/dashboard'); }} className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted cursor-pointer">Painel Admin</button>
                          <button onClick={() => { setMenuOpen(false); navigate('/admin/logs'); }} className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted cursor-pointer">Logs</button>
                          <button onClick={() => { setMenuOpen(false); navigate('/admin/feedbacks'); }} className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted cursor-pointer">Feedbacks</button>
                          <button onClick={() => { setMenuOpen(false); navigate('/admin/usuarios'); }} className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted cursor-pointer">Usuários</button>
                          <button onClick={() => { setMenuOpen(false); navigate('/admin/cub'); }} className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted cursor-pointer">CUB</button>
                          <button onClick={() => { setMenuOpen(false); navigate('/admin/sinapi'); }} className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted cursor-pointer">SINAPI</button>
                          <button onClick={() => { setMenuOpen(false); navigate('/admin/blog'); }} className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted cursor-pointer">Blog</button>
                        </>
                      )}
                      <div className="border-t border-border my-1" />
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 cursor-pointer"
                      >
                        Sair
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </nav>

        {/* Mobile menu button */}
        <button
          className="md:hidden p-2 text-secondary cursor-pointer"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background border-b border-border px-4 pb-4">
          {!user && (
            <>
              <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="block py-3 text-sm font-medium text-muted-foreground hover:text-secondary no-underline">Blog</Link>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-3 text-sm font-medium text-muted-foreground hover:text-secondary no-underline">Entrar</Link>
              <Link to="/orcamento/novo" onClick={() => setMobileMenuOpen(false)} className="no-underline">
                <button className="btn-cta text-sm px-5 py-2.5 w-full mt-2">Calcular Agora</button>
              </Link>
            </>
          )}
          {user && (
            <>
              <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="block py-3 text-sm font-medium text-muted-foreground hover:text-secondary no-underline">Blog</Link>
              <Link to="/meus-orcamentos" onClick={() => setMobileMenuOpen(false)} className="block py-3 text-sm font-medium text-muted-foreground hover:text-secondary no-underline">Meus Orçamentos</Link>
              <Link to="/planos" onClick={() => setMobileMenuOpen(false)} className="block py-3 text-sm font-medium text-muted-foreground hover:text-secondary no-underline">Planos</Link>
              {plan.id === 'admin' && (
                <Link to="/admin/dashboard" onClick={() => setMobileMenuOpen(false)} className="block py-3 text-sm font-medium text-muted-foreground hover:text-secondary no-underline">Painel Admin</Link>
              )}
              <Link to="/orcamento/novo" onClick={() => setMobileMenuOpen(false)} className="no-underline">
                <button className="btn-cta text-sm px-5 py-2.5 w-full mt-2">Calcular Agora</button>
              </Link>
              <button onClick={() => { setMobileMenuOpen(false); handleLogout(); }} className="block w-full text-left py-3 text-sm font-medium text-destructive cursor-pointer">Sair</button>
            </>
          )}
        </div>
      )}
    </header>
  );
}

export default Header;
